import { useState, useCallback, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { futebolRepository } from '../../data/futebolRepository';
import { obterOuCriarSessionId } from '@/shared/lib/jukeboxSession';
import { useBolaoAoVivo } from '../hooks/useBolaoAoVivo';

interface Props {
  telaId: string;
  fixtureId: number;
  tempoJogo: string | null;
  nomeCasa?: string;
  nomeVisitante?: string;
  escudoCasa?: string | null;
  escudoVisitante?: string | null;
}

// ── Escudo pequeno ─────────────────────────────────────────────────────────────

function EscudoPequeno({ src, nome }: { src?: string | null; nome?: string }) {
  const [erro, setErro] = useState(false);
  if (!src || erro) {
    return (
      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/40 text-[10px] font-bold uppercase">
        {nome?.slice(0, 2) ?? '??'}
      </div>
    );
  }
  return (
    <img src={src} alt={nome} className="w-8 h-8 object-contain" onError={() => setErro(true)} />
  );
}

// ── Componente principal ───────────────────────────────────────────────────────

export default function MobileBolao({
  telaId,
  fixtureId,
  tempoJogo,
  nomeCasa,
  nomeVisitante,
  escudoCasa,
  escudoVisitante,
}: Props) {
  const [palpiteCasa,      setPalpiteCasa]      = useState(0);
  const [palpiteVisitante, setPalpiteVisitante] = useState(0);
  const [mesa,             setMesa]             = useState<number | ''>('');
  const [loading,          setLoading]          = useState(false);
  const [jaPalpitou,       setJaPalpitou]       = useState(false);
  const [mesaBloqueada,    setMesaBloqueada]    = useState(false);
  const [erroEnvio,        setErroEnvio]        = useState<string | null>(null);
  const [pressedBtn,       setPressedBtn]       = useState<string | null>(null);

  const [meuSessionId] = useState<string>(() => obterOuCriarSessionId(telaId));
  const { palpites: todosPalpites, adicionarPalpite, erroLista } = useBolaoAoVivo(telaId, fixtureId);

  const mesas = Array.from({ length: 35 }, (_, i) => i + 1);

  const isAberto =
    !tempoJogo ||
    tempoJogo === 'NS' ||
    tempoJogo === 'HT' ||
    (/^\d+$/.test(tempoJogo) && parseInt(tempoJogo) <= 45);

  // ── Inicialização ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!fixtureId || !telaId) {
      setJaPalpitou(false);
      return;
    }
    const salvo = localStorage.getItem(`ds:bolao_${telaId}_${fixtureId}`)
      ?? localStorage.getItem(`ds:bolao_${fixtureId}`);
    setJaPalpitou(!!salvo);
    setPalpiteCasa(0);
    setPalpiteVisitante(0);
    setMesa('');
    setMesaBloqueada(false);
    setErroEnvio(null);
  }, [fixtureId, telaId]);

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!fixtureId || !telaId || mesa === '') return;
    setLoading(true);
    setErroEnvio(null);
    try {
      await futebolRepository.inserirPalpiteBolao({
        tela_id:           telaId,
        fixture_id:        fixtureId,
        session_id:        meuSessionId,
        identificacao:     `Mesa ${String(mesa).padStart(2, '0')}`,
        palpite_casa:      palpiteCasa,
        palpite_visitante: palpiteVisitante,
      });

      localStorage.setItem(`ds:bolao_${telaId}_${fixtureId}`, 'true');

      // Snapshot local imediato (o Realtime CDC confirma em seguida)
      adicionarPalpite({
        id:               crypto.randomUUID(),
        tela_id:          telaId,
        fixture_id:       fixtureId,
        session_id:       meuSessionId,
        identificacao:    `Mesa ${String(mesa).padStart(2, '0')}`,
        palpite_casa:     palpiteCasa,
        palpite_visitante: palpiteVisitante,
        created_at:       new Date().toISOString(),
      });

      setJaPalpitou(true);
    } catch (err) {
      const pgCode = (err as { code?: string })?.code;
      if (pgCode === '23505') {
        setMesaBloqueada(true);
        return;
      }
      console.error('[Bolao] Erro ao enviar palpite:', err);
      setErroEnvio('Não foi possível registrar seu palpite. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [mesa, telaId, fixtureId, meuSessionId, palpiteCasa, palpiteVisitante, adicionarPalpite]);

  // ── Render: Bolão encerrado ──────────────────────────────────────────────────

  if (!isAberto && !jaPalpitou) {
    return (
      <div className="flex flex-col items-center justify-center p-6 bg-[#0a0a0f] text-center rounded-2xl border border-white/5 mx-4 my-2">
        <div className="w-12 h-12 rounded-full bg-rose-500/10 flex items-center justify-center mb-3">
          <Lock size={22} className="text-rose-400/70" />
        </div>
        <h3 className="text-white/80 font-black tracking-wide text-sm">Bolão Encerrado</h3>
        <p className="text-white/40 text-xs mt-1">As apostas só são permitidas até o intervalo.</p>
      </div>
    );
  }

  // ── Render: Já votou — palpite próprio + lista ───────────────────────────────

  if (jaPalpitou) {
    const meuPalpite = todosPalpites.find((p) => p.session_id === meuSessionId) ?? null;

    return (
      <div className="flex flex-col bg-[#0a0a0f] rounded-2xl border border-white/10 mx-4 my-2 overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2 bg-white/[0.04]">
          <p className="text-[11px] font-bold uppercase tracking-widest text-amber-400">
            Bolão do Salão
          </p>
          <span className="text-[11px] text-white/30">
            {todosPalpites.length} {todosPalpites.length === 1 ? 'palpite' : 'palpites'}
          </span>
        </div>

        {/* Meu palpite em destaque */}
        {meuPalpite && (
          <div className="mx-3 mt-3 p-3 rounded-xl border border-amber-500/30 bg-amber-500/5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-400/80 mb-3">
              ★ Seu Palpite — {meuPalpite.identificacao}
            </p>
            <div className="flex items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-1.5">
                <EscudoPequeno src={escudoCasa} nome={nomeCasa} />
                <span className="text-white/50 text-[9px] uppercase font-semibold tracking-wide">
                  {nomeCasa ?? 'Casa'}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-white font-black text-4xl tabular-nums leading-none">
                  {meuPalpite.palpite_casa}
                </span>
                <span className="text-white/20 font-bold text-xl leading-none">×</span>
                <span className="text-white font-black text-4xl tabular-nums leading-none">
                  {meuPalpite.palpite_visitante}
                </span>
              </div>
              <div className="flex flex-col items-center gap-1.5">
                <EscudoPequeno src={escudoVisitante} nome={nomeVisitante} />
                <span className="text-white/50 text-[9px] uppercase font-semibold tracking-wide">
                  {nomeVisitante ?? 'Visitante'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Lista de todos os palpites */}
        <div className="px-3 pt-3 pb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-2 px-1">
            Palpites da galera
          </p>
          {erroLista && (
            <p className="text-center text-white/50 text-xs py-4">{erroLista}</p>
          )}
          {todosPalpites.length === 0 && !erroLista ? (
            <p className="text-center text-white/20 text-xs py-4">Sem palpites ainda</p>
          ) : todosPalpites.length > 0 ? (
            <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1">
              {todosPalpites.map((p) => {
                const ehMeu = p.session_id === meuSessionId;
                return (
                  <div
                    key={p.id}
                    className={`flex items-center justify-between px-3 py-2 rounded-xl border ${
                      ehMeu
                        ? 'bg-amber-500/10 border-amber-500/20'
                        : 'bg-white/[0.03] border-white/[0.05]'
                    }`}
                  >
                    <span
                      className={`text-xs font-bold ${
                        ehMeu ? 'text-amber-400' : 'text-white/50'
                      }`}
                    >
                      {ehMeu ? '★ ' : ''}{p.identificacao}
                    </span>
                    <span className="text-white font-black text-sm tabular-nums">
                      {p.palpite_casa} × {p.palpite_visitante}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // ── Render: Formulário de aposta ─────────────────────────────────────────────

  return (
    <div className="flex flex-col select-none bg-[#0a0a0f] rounded-2xl border border-white/10 mx-4 my-2 overflow-hidden shadow-2xl">
      <div className="flex items-center justify-between px-4 pt-3 pb-2 bg-white/5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-amber-400">
          Bolão do Salão
        </p>
      </div>

      {erroLista && (
        <p className="text-center text-white/50 text-xs py-4">{erroLista}</p>
      )}

      <div className="px-4 py-5 flex items-center justify-between gap-2">
        {/* CASA */}
        <div className="flex flex-col items-center flex-1">
          {escudoCasa ? (
            <img src={escudoCasa} alt={nomeCasa} className="w-10 h-10 object-contain mb-2" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/40 font-bold text-xs mb-2">
              {nomeCasa?.slice(0, 2)}
            </div>
          )}
          <span className="text-white/70 text-[10px] font-semibold uppercase truncate w-full text-center mb-2">
            {nomeCasa ?? 'Casa'}
          </span>
          <div className="flex items-center gap-2 bg-black/40 p-1.5 rounded-lg border border-white/5">
            <button
              onPointerDown={() => { if (palpiteCasa > 0) { setPressedBtn('casa-dec'); setPalpiteCasa(p => p - 1); } }}
              onPointerUp={() => setPressedBtn(null)}
              onPointerCancel={() => setPressedBtn(null)}
              onPointerLeave={() => setPressedBtn(null)}
              disabled={palpiteCasa === 0}
              className={`w-11 h-11 flex items-center justify-center rounded text-white text-lg transition-all duration-75 ${
                palpiteCasa === 0
                  ? 'opacity-25 cursor-not-allowed bg-white/5'
                  : pressedBtn === 'casa-dec' ? 'bg-white/15 scale-90' : 'bg-white/5'
              }`}
            >
              −
            </button>
            <span className="w-6 text-center text-xl font-black text-white tabular-nums">
              {palpiteCasa}
            </span>
            <button
              onPointerDown={() => { if (palpiteCasa < 99) { setPressedBtn('casa-inc'); setPalpiteCasa(p => p + 1); } }}
              onPointerUp={() => setPressedBtn(null)}
              onPointerCancel={() => setPressedBtn(null)}
              onPointerLeave={() => setPressedBtn(null)}
              disabled={palpiteCasa >= 99}
              className={`w-11 h-11 flex items-center justify-center rounded text-white text-lg transition-all duration-75 ${
                palpiteCasa >= 99
                  ? 'opacity-25 cursor-not-allowed bg-white/5'
                  : pressedBtn === 'casa-inc' ? 'bg-white/15 scale-90' : 'bg-white/5'
              }`}
            >
              +
            </button>
          </div>
        </div>

        {/* × */}
        <div className="shrink-0 text-white/20 font-black text-xl px-2">×</div>

        {/* VISITANTE */}
        <div className="flex flex-col items-center flex-1">
          {escudoVisitante ? (
            <img src={escudoVisitante} alt={nomeVisitante} className="w-10 h-10 object-contain mb-2" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/40 font-bold text-xs mb-2">
              {nomeVisitante?.slice(0, 2)}
            </div>
          )}
          <span className="text-white/70 text-[10px] font-semibold uppercase truncate w-full text-center mb-2">
            {nomeVisitante ?? 'Visitante'}
          </span>
          <div className="flex items-center gap-2 bg-black/40 p-1.5 rounded-lg border border-white/5">
            <button
              onPointerDown={() => { if (palpiteVisitante > 0) { setPressedBtn('visit-dec'); setPalpiteVisitante(p => p - 1); } }}
              onPointerUp={() => setPressedBtn(null)}
              onPointerCancel={() => setPressedBtn(null)}
              onPointerLeave={() => setPressedBtn(null)}
              disabled={palpiteVisitante === 0}
              className={`w-11 h-11 flex items-center justify-center rounded text-white text-lg transition-all duration-75 ${
                palpiteVisitante === 0
                  ? 'opacity-25 cursor-not-allowed bg-white/5'
                  : pressedBtn === 'visit-dec' ? 'bg-white/15 scale-90' : 'bg-white/5'
              }`}
            >
              −
            </button>
            <span className="w-6 text-center text-xl font-black text-white tabular-nums">
              {palpiteVisitante}
            </span>
            <button
              onPointerDown={() => { if (palpiteVisitante < 99) { setPressedBtn('visit-inc'); setPalpiteVisitante(p => p + 1); } }}
              onPointerUp={() => setPressedBtn(null)}
              onPointerCancel={() => setPressedBtn(null)}
              onPointerLeave={() => setPressedBtn(null)}
              disabled={palpiteVisitante >= 99}
              className={`w-11 h-11 flex items-center justify-center rounded text-white text-lg transition-all duration-75 ${
                palpiteVisitante >= 99
                  ? 'opacity-25 cursor-not-allowed bg-white/5'
                  : pressedBtn === 'visit-inc' ? 'bg-white/15 scale-90' : 'bg-white/5'
              }`}
            >
              +
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col gap-3">
        <div className="relative">
          <select
            value={mesa}
            onChange={(e) => {
              const val = e.target.value === '' ? '' : Number(e.target.value);
              setMesa(val);
              setMesaBloqueada(false);
              setErroEnvio(null);
              if (val !== '' && fixtureId) {
                const id = `Mesa ${String(val).padStart(2, '0')}`;
                futebolRepository.mesaJaApostou(telaId ?? '', fixtureId, id)
                  .then(bloqueada => setMesaBloqueada(bloqueada))
                  .catch(() => {});
              }
            }}
            className="w-full bg-black/50 border border-white/10 rounded-xl pl-3 pr-8 h-11 text-white text-sm outline-none appearance-none"
          >
            <option value="" disabled>Qual a sua mesa?</option>
            {mesas.map(m => (
              <option key={m} value={m}>Mesa {String(m).padStart(2, '0')}</option>
            ))}
          </select>
          <svg
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {mesaBloqueada && (
          <p className="text-rose-400 text-xs text-center font-semibold">
            Esta mesa já fez sua aposta neste jogo!
          </p>
        )}

        {erroEnvio && (
          <p className="text-rose-400 text-xs text-center font-semibold">{erroEnvio}</p>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading || mesa === '' || mesaBloqueada}
          className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl shadow-[0_6px_20px_rgba(245,158,11,0.25)] active:scale-[0.98] transition-transform disabled:opacity-50 disabled:scale-100 flex items-center justify-center"
        >
          {loading ? 'Enviando...' : 'CRAVAR PALPITE'}
        </button>
      </div>
    </div>
  );
}
